import React, { useEffect, useState, createContext, useContext } from 'react';
import { UserProfile } from '../types';
interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isOnboarding: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithSSO: (provider: 'google' | 'apple') => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  completeOnboarding: (profile: Partial<UserProfile>) => void;
  logout: () => void;
  updateProfile: (profile: Partial<UserProfile>) => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [user, setUser] = useState<UserProfile | null>(() => {
    const saved = localStorage.getItem('showpilot_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [isOnboarding, setIsOnboarding] = useState(() => {
    const saved = localStorage.getItem('showpilot_onboarding');
    return saved === 'true';
  });
  const isAuthenticated = user !== null;
  const login = async (email: string, password: string) => {
    // Simulate API call
    const mockUser: UserProfile = {
      id: '1',
      name: '',
      email,
      company: '',
      role: '',
      interests: [],
      defaultEvent: 'muscle'
    };
    setUser(mockUser);
    setIsOnboarding(true);
    localStorage.setItem('showpilot_user', JSON.stringify(mockUser));
    localStorage.setItem('showpilot_onboarding', 'true');
  };
  const loginWithSSO = async (provider: 'google' | 'apple') => {
    // Simulate SSO login
    const mockUser: UserProfile = {
      id: '1',
      name: provider === 'google' ? 'Google User' : 'Apple User',
      email: `user@${provider}.com`,
      company: '',
      role: '',
      interests: [],
      defaultEvent: 'muscle'
    };
    setUser(mockUser);
    setIsOnboarding(true);
    localStorage.setItem('showpilot_user', JSON.stringify(mockUser));
    localStorage.setItem('showpilot_onboarding', 'true');
  };
  const signup = async (email: string, password: string) => {
    // Simulate signup
    const mockUser: UserProfile = {
      id: '1',
      name: '',
      email,
      company: '',
      role: '',
      interests: [],
      defaultEvent: 'muscle'
    };
    setUser(mockUser);
    setIsOnboarding(true);
    localStorage.setItem('showpilot_user', JSON.stringify(mockUser));
    localStorage.setItem('showpilot_onboarding', 'true');
  };
  const completeOnboarding = (profile: Partial<UserProfile>) => {
    if (user) {
      const updatedUser = {
        ...user,
        ...profile
      };
      setUser(updatedUser);
      setIsOnboarding(false);
      localStorage.setItem('showpilot_user', JSON.stringify(updatedUser));
      localStorage.setItem('showpilot_onboarding', 'false');
      localStorage.setItem('showpilot_default_event', profile.defaultEvent || 'muscle');
    }
  };
  const logout = () => {
    setUser(null);
    setIsOnboarding(false);
    localStorage.removeItem('showpilot_user');
    localStorage.removeItem('showpilot_onboarding');
    localStorage.removeItem('showpilot_default_event');
  };
  const updateProfile = (profile: Partial<UserProfile>) => {
    if (user) {
      const updatedUser = {
        ...user,
        ...profile
      };
      setUser(updatedUser);
      localStorage.setItem('showpilot_user', JSON.stringify(updatedUser));
      if (profile.defaultEvent) {
        localStorage.setItem('showpilot_default_event', profile.defaultEvent);
      }
    }
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated,
    isOnboarding,
    login,
    loginWithSSO,
    signup,
    completeOnboarding,
    logout,
    updateProfile
  }} data-id="element-4266">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}